import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { createElement as _createElement } from "react";
import { AutoComplete, Col, Form, Input, Row } from "antd";
import { useCallback, useMemo } from "react";
import { TRANSLATIONS_PROPERTY_NAME } from "@devowl-wp/api";
import { DEFAULT_LANGUAGE, EUserRole, ServiceTemplateField } from "@devowl-wp/api-real-cookie-banner";
import { findExampleFromGroups, getKnownRegexpOptions, renderGroupNameTitle, renderKnownRegexpItem } from "./util.js";
import { useStores } from "../../../../../../../store/root.js";
import { mergeStyles } from "../../../../../../../util/style.js";
import { getInputPropsByLanguage, getTranslationFlagElementStyle, hasTranslationFlagsChanged, onTranslationValueChange } from "../../../../../../../util/translation.js";
import { ColumnToolTipHeader } from "../../../../../../table/column-tooltip-header.js";
import { QuillEditorFormField } from "../../../../../../third-party/quill-editor-form.js";
import { LanguageIcon } from "../../../fields/translation/language-icon-addon/language-icon.js";
import { TranslationInputFormFields } from "../../../fields/translation/language-input/language-input-form-fields.js";
import { TranslationQuillFormFields } from "../../../fields/translation/language-quill-input/language-quill-form-fields.js";
import { TranslationTextAreaFormFields } from "../../../fields/translation/language-text-area-input/language-text-area-form-fields.js";
function useServiceTemplateDynamicFieldTableColumns(t) {
    const { rcbAuthenticationStore, rcbUserSettingsStore: { settings: { translationSettings } } } = useStores();
    const form = Form.useFormInstance();
    const hasCreatorRole = rcbAuthenticationStore.hasRole(EUserRole.Creator);
    const knownRegexp = useMemo(()=>getKnownRegexpOptions(t), [
        t
    ]);
    const options = useMemo(()=>{
        return knownRegexp.map((group)=>{
            return {
                label: renderGroupNameTitle(group.title),
                options: group.options.map((option)=>renderKnownRegexpItem(option))
            };
        }).flat();
    }, [
        knownRegexp
    ]);
    const testExampleValue = useCallback(async (example, expression)=>{
        if (example && expression) {
            // test expression on sample value!
            const isValid = new RegExp(expression).test(example);
            if (!isValid) {
                return Promise.reject(new Error(t("Please adjust your expression {{exp}} or your sample value {{sample}}.", {
                    exp: expression,
                    sample: example
                })));
            }
        }
        return Promise.resolve();
    }, []);
    return [
        {
            title: /*#__PURE__*/ _jsx(ColumnToolTipHeader, {
                columnName: t("Variable Name"),
                tooltipTitle: t("Unique variable identifier for this field")
            }),
            dataIndex: "name",
            render: (_, record, index)=>{
                return /*#__PURE__*/ _jsx(Form.Item, {
                    noStyle: true,
                    shouldUpdate: (prev, next)=>hasTranslationFlagsChanged(prev, next, [
                            ServiceTemplateField.DynamicFields,
                            record.name,
                            "name"
                        ]),
                    children: ({ validateFields })=>{
                        return /*#__PURE__*/ _createElement(Form.Item, {
                            ...record,
                            shouldUpdate: (prev, next)=>{
                                const previousNames = useToConcatenatedNames(prev?.dynamicFields);
                                const nextNames = useToConcatenatedNames(next?.dynamicFields);
                                const should = prev?.dynamicFields[record.name] !== next?.dynamicFields[record.name] || previousNames !== nextNames;
                                return should;
                            },
                            key: `name-${index}`,
                            name: [
                                record.name,
                                "name"
                            ],
                            rules: [
                                ({ getFieldValue })=>({
                                        validator: (_rule, value)=>{
                                            const dynamicFields = getFieldValue(ServiceTemplateField.DynamicFields);
                                            const duplicate = dynamicFields.find((field, fieldIndex)=>{
                                                return fieldIndex !== index && field.name?.toLowerCase() === value?.toLowerCase();
                                            });
                                            if (duplicate) {
                                                return Promise.reject(new Error(t("Please give a unique name for this field.")));
                                            } else {
                                                return Promise.resolve();
                                            }
                                        }
                                    }),
                                {
                                    required: true,
                                    message: t("Please enter a variable name.")
                                }
                            ],
                            noStyle: true
                        }, /*#__PURE__*/ _jsx(Input, {
                            style: {
                                maxWidth: "100%"
                            },
                            onBlur: ()=>validateFields([
                                    ServiceTemplateField.CodeOptIn,
                                    ServiceTemplateField.CodeOptOut,
                                    ServiceTemplateField.CodeOnPageLoad
                                ]),
                            disabled: !hasCreatorRole
                        }));
                    }
                });
            }
        },
        {
            title: /*#__PURE__*/ _jsx(ColumnToolTipHeader, {
                columnName: t("Label"),
                tooltipTitle: t("Human readable label for this dynamic field variable")
            }),
            dataIndex: "label",
            render: (_, record, index)=>{
                return /*#__PURE__*/ _jsx(Form.Item, {
                    noStyle: true,
                    shouldUpdate: (prev, next)=>hasTranslationFlagsChanged(prev, next, [
                            ServiceTemplateField.DynamicFields,
                            record.name,
                            "label"
                        ]),
                    children: ({ getFieldValue })=>{
                        const relativeNamePath = [
                            record.name,
                            "label"
                        ];
                        const absoluteNamePath = [
                            ServiceTemplateField.DynamicFields,
                            record.name,
                            "label"
                        ];
                        const baseStyle = {
                            maxWidth: "100%"
                        };
                        const inputProps = getInputPropsByLanguage(DEFAULT_LANGUAGE, translationSettings);
                        inputProps.onChange = ()=>onTranslationValueChange(form, absoluteNamePath);
                        inputProps.style = mergeStyles(baseStyle, inputProps.style);
                        // merge styles
                        const translationFlagStyle = getTranslationFlagElementStyle(form, absoluteNamePath);
                        inputProps.style = mergeStyles(inputProps.style, translationFlagStyle);
                        // create language icon
                        inputProps.suffix = /*#__PURE__*/ _jsx(LanguageIcon, {
                            lang: DEFAULT_LANGUAGE,
                            flexPathConfig: {
                                isFormListItem: true,
                                relative: {
                                    namePath: relativeNamePath,
                                    namePathPrefix: ServiceTemplateField.DynamicFields,
                                    customFlagNamePath: (lang)=>[
                                            ServiceTemplateField.DynamicFields,
                                            record.name,
                                            TRANSLATIONS_PROPERTY_NAME,
                                            lang,
                                            "label"
                                        ],
                                    translationNamePath: (lang)=>[
                                            ServiceTemplateField.DynamicFields,
                                            record.name,
                                            TRANSLATIONS_PROPERTY_NAME,
                                            lang,
                                            "label"
                                        ]
                                }
                            }
                        });
                        return /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _createElement(Form.Item, {
                                    ...record,
                                    key: `label-${index}`,
                                    name: relativeNamePath,
                                    noStyle: true
                                }, /*#__PURE__*/ _jsx(Input, {
                                    ...inputProps,
                                    disabled: !hasCreatorRole
                                })),
                                /*#__PURE__*/ _jsx(TranslationInputFormFields, {
                                    flexPathConfig: {
                                        isFormListItem: true,
                                        relative: {
                                            namePath: (lang)=>[
                                                    record.name,
                                                    TRANSLATIONS_PROPERTY_NAME,
                                                    lang,
                                                    "label"
                                                ],
                                            customFlagNamePath: (lang)=>[
                                                    ServiceTemplateField.DynamicFields,
                                                    record.name,
                                                    TRANSLATIONS_PROPERTY_NAME,
                                                    lang,
                                                    "label"
                                                ]
                                        }
                                    },
                                    inputProps: {
                                        maxLength: 255,
                                        style: baseStyle
                                    }
                                })
                            ]
                        });
                    }
                }, `label-row-${index}`);
            }
        },
        {
            title: /*#__PURE__*/ _jsx(ColumnToolTipHeader, {
                columnName: t("Expression"),
                tooltipTitle: t("Define value format via regular expression, like: /^UA-d+-d+/")
            }),
            dataIndex: "expression",
            render: (_, record, index)=>{
                return /*#__PURE__*/ _jsx(Form.Item, {
                    noStyle: true,
                    shouldUpdate: (prev, next)=>hasTranslationFlagsChanged(prev, next, [
                            ServiceTemplateField.DynamicFields,
                            record.name,
                            "expression"
                        ]),
                    children: ({ setFieldValue })=>{
                        return /*#__PURE__*/ _jsx(Form.Item, {
                            name: [
                                record.name,
                                "expression"
                            ],
                            dependencies: [
                                [
                                    ServiceTemplateField.DynamicFields,
                                    record.name,
                                    "example"
                                ]
                            ],
                            noStyle: true,
                            children: /*#__PURE__*/ _jsx(AutoComplete, {
                                options: options,
                                style: {
                                    maxWidth: "100%"
                                },
                                showSearch: false,
                                popupMatchSelectWidth: 1000,
                                onSelect: (_)=>{
                                    const found = findExampleFromGroups(_, knownRegexp);
                                    if (found) {
                                        setFieldValue([
                                            ServiceTemplateField.DynamicFields,
                                            record.name,
                                            "example"
                                        ], found);
                                    }
                                },
                                disabled: !hasCreatorRole,
                                children: /*#__PURE__*/ _jsx(Input, {
                                    disabled: !hasCreatorRole
                                })
                            })
                        }, `expression-${index}`);
                    }
                }, `expression-item-${index}`);
            }
        },
        {
            title: /*#__PURE__*/ _jsx(ColumnToolTipHeader, {
                columnName: t("Example"),
                tooltipTitle: t("Should show the standard format of the variable with example characters. For example G-123456789.")
            }),
            dataIndex: "example",
            render: (_, record, index)=>{
                return /*#__PURE__*/ _jsx(Form.Item, {
                    noStyle: true,
                    shouldUpdate: (prev, next)=>hasTranslationFlagsChanged(prev, next, [
                            ServiceTemplateField.DynamicFields,
                            record.name,
                            "example"
                        ]),
                    children: ({ getFieldValue })=>{
                        const exampleRules = [
                            {
                                validator (_rule, value) {
                                    const rowData = getFieldValue([
                                        ServiceTemplateField.DynamicFields,
                                        record.name
                                    ]);
                                    return testExampleValue(value, rowData?.expression);
                                }
                            }
                        ];
                        const relativeExamplePath = [
                            record.name,
                            "example"
                        ];
                        const absoluteExamplePath = [
                            ServiceTemplateField.DynamicFields,
                            record.name,
                            "example"
                        ];
                        const inputProps = getInputPropsByLanguage(DEFAULT_LANGUAGE, translationSettings);
                        inputProps.onChange = ()=>onTranslationValueChange(form, absoluteExamplePath);
                        // merge styles
                        const translationFlagStyle = getTranslationFlagElementStyle(form, absoluteExamplePath);
                        inputProps.style = mergeStyles(inputProps.style, translationFlagStyle);
                        // create language icon
                        inputProps.suffix = /*#__PURE__*/ _jsx(LanguageIcon, {
                            lang: DEFAULT_LANGUAGE,
                            flexPathConfig: {
                                isFormListItem: true,
                                relative: {
                                    namePath: relativeExamplePath,
                                    namePathPrefix: ServiceTemplateField.DynamicFields,
                                    customFlagNamePath: (lang)=>[
                                            ServiceTemplateField.DynamicFields,
                                            record.name,
                                            TRANSLATIONS_PROPERTY_NAME,
                                            lang,
                                            "example"
                                        ],
                                    translationNamePath: (lang)=>[
                                            ServiceTemplateField.DynamicFields,
                                            record.name,
                                            TRANSLATIONS_PROPERTY_NAME,
                                            lang,
                                            "example"
                                        ]
                                }
                            }
                        });
                        return /*#__PURE__*/ _jsx(_Fragment, {
                            children: /*#__PURE__*/ _jsx(Row, {
                                gutter: [
                                    10,
                                    10
                                ],
                                children: /*#__PURE__*/ _jsxs(Col, {
                                    flex: 1,
                                    children: [
                                        /*#__PURE__*/ _createElement(Form.Item, {
                                            ...record,
                                            key: `example-${index}`,
                                            name: relativeExamplePath,
                                            dependencies: [
                                                [
                                                    ServiceTemplateField.DynamicFields,
                                                    record.name,
                                                    "expression"
                                                ]
                                            ],
                                            rules: exampleRules,
                                            hasFeedback: true,
                                            noStyle: true
                                        }, /*#__PURE__*/ _jsx(Input, {
                                            style: {
                                                maxWidth: "100%"
                                            },
                                            ...inputProps,
                                            disabled: !hasCreatorRole
                                        })),
                                        /*#__PURE__*/ _jsx(TranslationInputFormFields, {
                                            flexPathConfig: {
                                                isFormListItem: true,
                                                relative: {
                                                    namePath: (lang)=>[
                                                            record.name,
                                                            TRANSLATIONS_PROPERTY_NAME,
                                                            lang,
                                                            "example"
                                                        ],
                                                    customFlagNamePath: (lang)=>[
                                                            ServiceTemplateField.DynamicFields,
                                                            record.name,
                                                            TRANSLATIONS_PROPERTY_NAME,
                                                            lang,
                                                            "example"
                                                        ]
                                                }
                                            },
                                            formProps: {
                                                dependencies: [
                                                    [
                                                        ServiceTemplateField.DynamicFields,
                                                        record.name,
                                                        "expression"
                                                    ]
                                                ],
                                                rules: exampleRules
                                            }
                                        })
                                    ]
                                })
                            })
                        });
                    }
                }, `example-item-${index}`);
            }
        },
        {
            title: t("Hint"),
            dataIndex: "hint",
            render: (_, record, index)=>{
                return /*#__PURE__*/ _jsx(Form.Item, {
                    noStyle: true,
                    shouldUpdate: (prev, next)=>hasTranslationFlagsChanged(prev, next, [
                            ServiceTemplateField.DynamicFields,
                            record.name,
                            "hint"
                        ]),
                    children: ({ getFieldValue })=>{
                        const relativeHintPath = [
                            record.name,
                            "hint"
                        ];
                        const absoluteHintPath = [
                            ServiceTemplateField.DynamicFields,
                            record.name,
                            "hint"
                        ];
                        const editorStyle = getTranslationFlagElementStyle(form, absoluteHintPath);
                        return /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsxs(Row, {
                                    gutter: [
                                        10,
                                        10
                                    ],
                                    style: {
                                        flexFlow: "row"
                                    },
                                    children: [
                                        /*#__PURE__*/ _jsx(Col, {
                                            flex: 1,
                                            children: /*#__PURE__*/ _jsx(QuillEditorFormField, {
                                                namePath: [
                                                    record.name,
                                                    "hint"
                                                ],
                                                quillEditorProps: {
                                                    readOnly: !hasCreatorRole,
                                                    style: editorStyle,
                                                    onChange: ()=>onTranslationValueChange(form, absoluteHintPath)
                                                }
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx(Col, {
                                            flex: "0",
                                            children: /*#__PURE__*/ _jsx(LanguageIcon, {
                                                lang: DEFAULT_LANGUAGE,
                                                flexPathConfig: {
                                                    relative: {
                                                        namePath: relativeHintPath,
                                                        namePathPrefix: ServiceTemplateField.DynamicFields
                                                    },
                                                    absolute: {
                                                        namePath: absoluteHintPath,
                                                        translationNamePath: (lang)=>[
                                                                ServiceTemplateField.DynamicFields,
                                                                record.name,
                                                                TRANSLATIONS_PROPERTY_NAME,
                                                                lang,
                                                                "hint"
                                                            ]
                                                    },
                                                    isFormListItem: true
                                                }
                                            })
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(TranslationQuillFormFields, {
                                    flexPathConfig: {
                                        isFormListItem: true,
                                        relative: {
                                            namePath: (lang)=>[
                                                    record.name,
                                                    TRANSLATIONS_PROPERTY_NAME,
                                                    lang,
                                                    "hint"
                                                ],
                                            customFlagNamePath: (lang)=>[
                                                    ServiceTemplateField.DynamicFields,
                                                    record.name,
                                                    TRANSLATIONS_PROPERTY_NAME,
                                                    lang,
                                                    "hint"
                                                ]
                                        }
                                    },
                                    quillEditorProps: {
                                        isSmallToolbarMenu: true
                                    }
                                })
                            ]
                        });
                    }
                }, `hint-item-${index}`);
            }
        },
        {
            title: /*#__PURE__*/ _jsx(ColumnToolTipHeader, {
                columnName: t("Invalid Message"),
                tooltipTitle: t("Message to display to users, when they enter invalid value")
            }),
            dataIndex: "invalidMessage",
            render: (_, record, index)=>{
                return /*#__PURE__*/ _jsx(Form.Item, {
                    shouldUpdate: (prev, next)=>hasTranslationFlagsChanged(prev, next, [
                            ServiceTemplateField.DynamicFields,
                            record.name,
                            "invalidMessage"
                        ]),
                    noStyle: true,
                    children: ({ getFieldValue })=>{
                        const relativePath = [
                            record.name,
                            "invalidMessage"
                        ];
                        const absolutePath = [
                            ServiceTemplateField.DynamicFields,
                            record.name,
                            "invalidMessage"
                        ];
                        const dynamicStyle = {
                            maxWidth: "100%",
                            ...getTranslationFlagElementStyle(form, absolutePath)
                        };
                        return /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsxs(Row, {
                                    gutter: [
                                        10,
                                        10
                                    ],
                                    style: {
                                        flexFlow: "nowrap"
                                    },
                                    children: [
                                        /*#__PURE__*/ _jsx(Col, {
                                            flex: 1,
                                            children: /*#__PURE__*/ _createElement(Form.Item, {
                                                ...record,
                                                key: `invalidMessage-${index}`,
                                                name: relativePath,
                                                noStyle: true
                                            }, /*#__PURE__*/ _jsx(Input.TextArea, {
                                                style: dynamicStyle,
                                                onChange: ()=>onTranslationValueChange(form, absolutePath),
                                                autoSize: {
                                                    minRows: 1,
                                                    maxRows: 2
                                                },
                                                disabled: !hasCreatorRole
                                            }))
                                        }),
                                        /*#__PURE__*/ _jsx(Col, {
                                            flex: "0",
                                            children: /*#__PURE__*/ _jsx(LanguageIcon, {
                                                lang: DEFAULT_LANGUAGE,
                                                flexPathConfig: {
                                                    isFormListItem: true,
                                                    relative: {
                                                        namePath: relativePath,
                                                        namePathPrefix: ServiceTemplateField.DynamicFields,
                                                        translationNamePath: (lang)=>[
                                                                ServiceTemplateField.DynamicFields,
                                                                record.name,
                                                                TRANSLATIONS_PROPERTY_NAME,
                                                                lang,
                                                                "invalidMessage"
                                                            ]
                                                    }
                                                }
                                            })
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(TranslationTextAreaFormFields, {
                                    flexPathConfig: {
                                        isFormListItem: true,
                                        relative: {
                                            namePath: (lang)=>[
                                                    record.name,
                                                    TRANSLATIONS_PROPERTY_NAME,
                                                    lang,
                                                    "invalidMessage"
                                                ],
                                            customFlagNamePath: (lang)=>[
                                                    ServiceTemplateField.DynamicFields,
                                                    record.name,
                                                    TRANSLATIONS_PROPERTY_NAME,
                                                    lang,
                                                    "invalidMessage"
                                                ]
                                        }
                                    },
                                    textAreaProps: {
                                        autoSize: {
                                            minRows: 1,
                                            maxRows: 3
                                        },
                                        style: {
                                            maxWidth: "100%"
                                        }
                                    }
                                })
                            ]
                        });
                    }
                }, `invalidMessage-item-${index}`);
            }
        }
    ];
}
function useToConcatenatedNames(dynamicFields, excludeIndex = []) {
    return dynamicFields?.map((field, fieldIndex)=>excludeIndex.includes(fieldIndex) ? undefined : field.name).filter(Boolean).join("");
}
export { useServiceTemplateDynamicFieldTableColumns };
